/**
 * WebRTC CDN播放器工具类
 * 用于大班课场景下，使用 TCPlayer 播放 CDN 流
 */

import { uploadLiveLog } from "@saas/components/src/utils/log";

// 检测微信环境
function isWeChatBrowser() {
  const ua = navigator.userAgent.toLowerCase();
  return /micromessenger|wechat/i.test(ua);
}

class WebRTCPlayer {
  constructor(options = {}) {
    this.container = options.container;
    this.url = options.url;
    this.onError = options.onError; // 错误回调
    this.onPlaying = options.onPlaying; // 播放回调
    this.onNeedUserInteraction = options.onNeedUserInteraction; // 需要用户交互回调
    this.player = null;
    this.videoElement = null;
    this.playerId =
      "webrtc-player-" +
      Date.now() +
      "-" +
      Math.random().toString(36).substr(2, 9);
    this.isPlaying = false;
    this.isMuted = false; // 当前静音状态
    // 性能追踪
    this.initStartTime = null;
    this.performanceData = {};
    // blocked 事件恢复状态追踪
    this.blockedRecoveryTimer = null; // blocked 恢复检测定时器
    this.blockedTriggered = false; // 标记 blocked 是否触发过
    this.blockedMutedPlaying = false; // 标记是否是 blocked 后的静音播放
    this.isWeChat = isWeChatBrowser(); // 微信环境标记
    this.autoplayFallbackTimer = null; // 自动播放降级定时器
  }

  /**
   * 记录关键性能节点并上传日志
   * @param {string} stage 阶段名称
   * @param {object} extraData 额外数据
   */
  logPerformanceStage(stage, extraData = {}) {
    const now = performance.now();
    const elapsed = this.initStartTime
      ? Number((now - this.initStartTime).toFixed(2))
      : 0;
    this.performanceData[stage] = elapsed;

    console.log(`[WebRTCPlayer][性能] ${stage}: +${elapsed}ms`);

    uploadLiveLog({
      message: `CDN播放器-${stage}`,
      level: "info",
      tag: "cdn_player_performance",
      extra: {
        actioin: "startCDNPlayer",
        component: "WebRTCPlayer",
        stage: stage,
        elapsed: elapsed,
        url: this.url,
        allStages: this.performanceData,
        ...extraData,
      },
    });
  }

  /**
   * 初始化播放器
   * @returns {Promise}
   */
  async init() {
    return new Promise((resolve, reject) => {
      try {
        // 关键节点1：初始化开始
        this.initStartTime = performance.now();
        this.logPerformanceStage("1_初始化开始");

        if (!this.container) {
          reject(new Error("容器元素不存在"));
          return;
        }

        // 检查是否已存在 TCPlayer
        if (window.TCPlayer) {
          this.useTCPlayer(resolve, reject);
        } else {
          // 使用原生 video 标签
          this.useNativeVideo(resolve, reject);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 使用腾讯云 TCPlayer
   */
  useTCPlayer(resolve, reject) {
    try {
      // 清空容器
      this.container.innerHTML = "";

      // 创建 video 元素
      this.videoElement = document.createElement("video");
      this.videoElement.id = this.playerId;
      this.videoElement.style.width = "100%";
      this.videoElement.style.height = "100%";
      this.videoElement.style.objectFit = "contain";
      this.videoElement.setAttribute("playsinline", "true");
      this.videoElement.setAttribute("webkit-playsinline", "true");
      this.videoElement.setAttribute("x5-playsinline", "true");
      // 保存 pause 事件处理函数引用，以便后续移除
      this.handlePause = () => {
        console.log("[WebRTCPlayer] 原生video pause");
        // 通过 TCPlayer API 播放，避免绕过 TCPlayer 内部状态管理导致视频黑屏
        if (this.player && typeof this.player.play === "function") {
          this.player.play();
        } else if (this.videoElement) {
          this.videoElement.play();
        }
        uploadLiveLog({
          message: "CDN播放器-pause",
          level: "warn",
          tag: "cdn_player_pause",
          extra: {
            actioin: "pause",
            component: "WebRTCPlayer",
          },
        });
      };
      this.videoElement.addEventListener("pause", this.handlePause);
      // 添加到容器
      this.container.appendChild(this.videoElement);

      // 关键节点2：TCPlayer SDK 初始化
      // 所有环境统一：尝试静音自动播放，失败后降级显示按钮
      console.log("[WebRTCPlayer] isWeChat:", this.isWeChat);

      this.player = window.TCPlayer(this.playerId, {
        sources: [
          {
            src: this.url,
          },
        ],
        autoplay: true,
        muted: false, // 优先有声自动播放，blocked 后降级静音
        preload: "auto",
        playbackRates: [1],
        controls: false,
        width: "100%",
        height: "100%",
        live: true,
        h5_flv: false,
        x5_player: true,
        x5_type: "h5",
        x5_fullscreen: false,
        wording: {
          2032: "请求视频失败，请检查网络",
          2048: "请求m3u8文件失败，可能是网络错误或者跨域问题",
        },
      });

      // 设置自动播放降级定时器：如果 5 秒内没有 playing 事件，说明自动播放失败
      // 显示按钮让用户手动播放
      this.autoplayFallbackTimer = setTimeout(() => {
        if (!this.isPlaying) {
          console.log("[WebRTCPlayer] 自动播放超时，降级显示播放按钮");
          // 重置静音播放标志，确保用户点击时走 play() 而非 unmute()
          this.blockedMutedPlaying = false;
          this.isMuted = false;
          if (this.onNeedUserInteraction && typeof this.onNeedUserInteraction === "function") {
            this.onNeedUserInteraction();
          }
          uploadLiveLog({
            message: "CDN播放器-自动播放超时降级",
            level: "warn",
            tag: "cdn_player_autoplay",
            extra: {
              component: "WebRTCPlayer",
              url: this.url,
              isWeChat: this.isWeChat,
            },
          });
        }
        this.autoplayFallbackTimer = null;
      }, 5000);

      this.logPerformanceStage("2_SDK初始化完成");
      this.player.on("webrtcevent", (event) => {
        console.log("[WebRTCPlayer] TCPlayer webrtcevent:", event);
        uploadLiveLog({
          message: "CDN播放器-webrtcevent",
          level: "log",
          tag: "cdn_player_webrtcevent",
          extra: {
            actioin: "webrtcevent",
            error: JSON.stringify(event?.data),
            component: "WebRTCPlayer",
            url: this.url,
          },
        });
      });
      this.player.on("blocked", (event) => {
        console.warn("[WebRTCPlayer] TCPlayer blocked - 尝试静音播放:", event);
        this.blockedTriggered = true;
        this.blockedMutedPlaying = true; // 标记为 blocked 后的静音播放

        // 清除之前的定时器，避免重复触发
        if (this.blockedRecoveryTimer) {
          clearTimeout(this.blockedRecoveryTimer);
          this.blockedRecoveryTimer = null;
        }

        // 尝试静音播放
        try {
          this.setMuted(true);
          this.isMuted = true;
          console.log("[WebRTCPlayer] blocked - 已静音，尝试播放");

          // 尝试播放（静音状态）— 通过 TCPlayer API
          if (this.player && typeof this.player.play === "function") {
            const playResult = this.player.play();
            if (playResult && typeof playResult.catch === "function") {
              playResult.catch((err) => {
                console.warn("[WebRTCPlayer] blocked后静音播放Promise被拒绝:", err?.name, err?.message);
              });
            }
          } else if (this.videoElement) {
            this.videoElement.play().catch((err) => {
              console.error("[WebRTCPlayer] 静音播放失败:", err);
            });
          }
        } catch (e) {
          console.error("[WebRTCPlayer] 设置静音失败:", e);
        }

        // 1.5秒后检查是否已经开始播放
        this.blockedRecoveryTimer = setTimeout(() => {
          // 再次检查播放状态（可能在这1.5秒内已经开始播放了）
          let isActuallyPlaying = false;
          try {
            if (this.player && typeof this.player.paused === "function") {
              const paused = this.player.paused();
              isActuallyPlaying = !paused;
            } else if (this.videoElement) {
              isActuallyPlaying =
                !this.videoElement.paused && !this.videoElement.ended;
            }
          } catch (e) {
            console.warn("[WebRTCPlayer] 检查播放状态失败:", e);
          }

          // 如果已经在播放（静音状态），通知上层显示取消静音按钮
          if (this.isPlaying || isActuallyPlaying) {
            console.log(
              "[WebRTCPlayer] blocked 后已静音播放，显示取消静音按钮"
            );

            if (
              this.onNeedUserInteraction &&
              typeof this.onNeedUserInteraction === "function"
            ) {
              this.onNeedUserInteraction();
            }

            uploadLiveLog({
              message: "CDN播放器-blocked-静音播放成功",
              level: "info",
              tag: "cdn_player_blocked",
              extra: {
                actioin: "blocked_muted_playing",
                error: JSON.stringify(event?.data),
                component: "WebRTCPlayer",
                url: this.url,
                isPlaying: this.isPlaying,
                isActuallyPlaying: isActuallyPlaying,
              },
            });
          } else {
            console.log("[WebRTCPlayer] blocked 后1.5秒仍未播放");
            // blocked 后静音播放也失败了，显示播放按钮
            if (
              this.onNeedUserInteraction &&
              typeof this.onNeedUserInteraction === "function"
            ) {
              this.onNeedUserInteraction();
            }
            uploadLiveLog({
              message: "CDN播放器-blocked-静音播放失败",
              level: "warn",
              tag: "cdn_player_blocked",
              extra: {
                actioin: "blocked_muted_failed",
                error: JSON.stringify(event?.data),
                component: "WebRTCPlayer",
                url: this.url,
              },
            });
          }
          this.blockedRecoveryTimer = null;
        }, 1500);

        uploadLiveLog({
          message: "CDN播放器-blocked-事件触发",
          level: "warn",
          tag: "cdn_player_blocked",
          extra: {
            actioin: "blocked",
            error: JSON.stringify(event?.data),
            component: "WebRTCPlayer",
            url: this.url,
          },
        });
      });
      // 监听播放器事件
      this.player.on("error", (error) => {
        console.error("[WebRTCPlayer] TCPlayer 播放错误:", error);

        // 处理错误提示
        const errorCode = error.code || (error.data && error.data.code);
        const errorMsg = "网络不稳定，建议检查网络设置或刷新直播间";
        if (this.onError && typeof this.onError === "function") {
          this.onError(errorMsg, error);
        }

        uploadLiveLog({
          message: "CDN播放器-播放错误",
          level: "error",
          tag: "cdn_player_performance",
          extra: {
            actioin: "startCDNPlayer",
            component: "WebRTCPlayer",
            errorCode: errorCode,
            errorMsg: errorMsg,
            error: error?.message || String(error),
            url: this.url,
            allStages: this.performanceData,
          },
        });
      });

      // 关键节点3：开始加载流
      this.player.on("loadstart", () => {
        this.logPerformanceStage("3_开始加载流");
      });

      // 关键节点4：元数据加载完成（CDN响应）
      this.player.on("loadedmetadata", () => {
        this.logPerformanceStage("4_元数据加载完成");
      });

      // 关键节点5：开始播放
      // 这个事件有可能在blocked之前触发，也有可能在blocked之后触发
      this.player.on("playing", () => {
        console.log("[WebRTCPlayer] TCPlayer playing - 开始播放");
        this.isPlaying = true;

        // 清除自动播放降级定时器
        if (this.autoplayFallbackTimer) {
          clearTimeout(this.autoplayFallbackTimer);
          this.autoplayFallbackTimer = null;
        }

        // 如果是静音自动播放成功，显示取消静音按钮
        // 双重校验：blockedMutedPlaying 标志 + 实际静音状态，避免有声自动播放成功时误显示按钮
        if (this.blockedMutedPlaying && this.isMuted) {
          console.log("[WebRTCPlayer] 静音自动播放成功，显示取消静音按钮");
          if (
            this.onNeedUserInteraction &&
            typeof this.onNeedUserInteraction === "function"
          ) {
            this.onNeedUserInteraction();
          }
        } else if (this.onPlaying && typeof this.onPlaying === "function") {
          // 非静音播放（用户已交互），隐藏按钮
          this.onPlaying();
        }

        this.logPerformanceStage("5_开始播放", {
          // 计算各阶段耗时
          sdkInitTime: this.performanceData["2_SDK初始化完成"] || 0,
          loadStartTime:
            (this.performanceData["3_开始加载流"] || 0) -
            (this.performanceData["2_SDK初始化完成"] || 0),
          metadataLoadTime:
            (this.performanceData["4_元数据加载完成"] || 0) -
            (this.performanceData["3_开始加载流"] || 0),
          playStartTime:
            (this.performanceData["5_开始播放"] || 0) -
            (this.performanceData["4_元数据加载完成"] || 0),
          blockedMutedPlaying: this.blockedMutedPlaying,
        });
      });

      // 监听等待/卡顿事件（可能影响播放体验）
      this.player.on("waiting", () => {
        console.log("[WebRTCPlayer] TCPlayer waiting - 等待数据");
      });

      this.player.on("stalled", () => {
        console.log("[WebRTCPlayer] TCPlayer stalled - 数据停滞");
      });

      resolve();
    } catch (error) {
      console.error("[WebRTCPlayer] TCPlayer 初始化失败:", error);
      reject(error);
    }
  }

  /**
   * 使用原生 video 标签
   */
  useNativeVideo(resolve, reject) {
    try {
      // 清空容器
      this.container.innerHTML = "";

      // 创建 video 元素
      this.videoElement = document.createElement("video");
      this.videoElement.style.width = "100%";
      this.videoElement.style.height = "100%";
      this.videoElement.style.objectFit = "cover";
      this.videoElement.autoplay = true;
      this.videoElement.muted = false;
      this.videoElement.playsInline = true;

      // 设置视频源
      this.videoElement.src = this.url;

      // 添加到容器
      this.container.appendChild(this.videoElement);

      // 监听播放事件
      this.videoElement.addEventListener("playing", () => {
        console.log("[WebRTCPlayer] 原生video开始播放");
        this.isPlaying = true;
        if (this.onPlaying && typeof this.onPlaying === "function") {
          this.onPlaying();
        }
      });

      this.videoElement.addEventListener("error", (error) => {
        console.error("[WebRTCPlayer] 原生video播放错误:", error);

        if (this.onError && typeof this.onError === "function") {
          this.onError("播放出错，请刷新重试", error);
        }
      });

      // 尝试播放
      this.videoElement
        .play()
        .then(() => {
          resolve();
        })
        .catch((error) => {
          console.error("[WebRTCPlayer] 播放失败:", error);
          reject(error);
        });
    } catch (error) {
      console.error("[WebRTCPlayer] 原生video初始化失败:", error);
      reject(error);
    }
  }

  /**
   * 播放
   * @returns {Promise}
   */
  async play() {
    return new Promise((resolve, reject) => {
      try {
        if (this.player && this.player.play) {
          this.player.play();
          resolve();
        } else if (this.videoElement) {
          this.videoElement
            .play()
            .then(() => {
              this.isPlaying = true;
              resolve();
            })
            .catch(reject);
        } else {
          reject(new Error("播放器未初始化"));
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 暂停
   * @returns {Promise}
   */
  async pause() {
    return new Promise((resolve, reject) => {
      try {
        if (this.player && this.player.pause) {
          this.player.pause();
          this.isPlaying = false;
          resolve();
        } else if (this.videoElement) {
          this.videoElement.pause();
          this.isPlaying = false;
          resolve();
        } else {
          reject(new Error("播放器未初始化"));
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * 停止播放
   * @returns {Promise}
   */
  async stop() {
    return new Promise((resolve) => {
      try {
        if (this.player && this.player.pause) {
          this.player.pause();
        } else if (this.videoElement) {
          this.videoElement.pause();
          this.videoElement.src = "";
        }
        this.isPlaying = false;
        this.isMuted = false;
        this.blockedMutedPlaying = false;
        resolve();
      } catch (error) {
        console.error("[WebRTCPlayer] 停止播放失败:", error);
        resolve();
      }
    });
  }

  /**
   * 销毁播放器
   */
  destroy() {
    try {
      // 先暂停播放
      this.isPlaying = false;
      this.isMuted = false;

      // 清理定时器
      if (this.blockedRecoveryTimer) {
        clearTimeout(this.blockedRecoveryTimer);
        this.blockedRecoveryTimer = null;
      }
      if (this.autoplayFallbackTimer) {
        clearTimeout(this.autoplayFallbackTimer);
        this.autoplayFallbackTimer = null;
      }

      // 重置 blocked 恢复状态
      this.blockedTriggered = false;
      this.blockedMutedPlaying = false;

      if (this.player) {
        // TCPlayer 使用 dispose 方法销毁
        try {
          if (typeof this.player.dispose === "function") {
            this.player.dispose();
          } else if (typeof this.player.destroy === "function") {
            this.player.destroy();
          }
        } catch (e) {
          console.error("[WebRTCPlayer] 销毁player实例失败:", e);
        }
        this.player = null;
      }

      if (this.videoElement) {
        // 停止播放并清除所有资源
        try {
          this.videoElement.pause();
          this.videoElement.removeAttribute("src");
          this.videoElement.load(); // 重新加载以释放资源
          // 移除所有事件监听器
          if (this.handlePause) {
            this.videoElement.removeEventListener("pause", this.handlePause);
            this.handlePause = null;
          }
          this.videoElement.onplaying = null;
          this.videoElement.onerror = null;
          // 从DOM中移除
          if (this.videoElement.parentNode) {
            this.videoElement.parentNode.removeChild(this.videoElement);
          }
        } catch (e) {
          console.error("[WebRTCPlayer] 清理video元素失败:", e);
        }
        this.videoElement = null;
      }

      if (this.container) {
        // 清空容器中的所有内容
        try {
          this.container.innerHTML = "";
        } catch (e) {
          console.error("[WebRTCPlayer] 清空容器失败:", e);
        }
      }

      console.log("[WebRTCPlayer] 播放器已销毁");
    } catch (error) {
      console.error("[WebRTCPlayer] 销毁播放器失败:", error);
    }
  }

  /**
   * 设置音量
   * @param {number} volume 0-1之间的值
   */
  setVolume(volume) {
    try {
      if (this.player) {
        // TCPlayer 使用 volume 方法
        if (typeof this.player.volume === "function") {
          this.player.volume(volume);
        }
      } else if (this.videoElement) {
        this.videoElement.volume = volume;
      }
    } catch (error) {
      console.error("[WebRTCPlayer] 设置音量失败:", error);
    }
  }

  /**
   * 静音/取消静音
   * @param {boolean} muted
   */
  setMuted(muted) {
    try {
      if (this.player) {
        // TCPlayer 使用 muted 方法
        if (typeof this.player.muted === "function") {
          this.player.muted(muted);
        }
      } else if (this.videoElement) {
        this.videoElement.muted = muted;
      }

      this.isMuted = muted;
      console.log(`[WebRTCPlayer] 静音状态已设置为: ${muted}`);

      // 触发静音状态回调
      if (this.onMuteChange && typeof this.onMuteChange === "function") {
        this.onMuteChange(muted);
      }

      uploadLiveLog({
        message: `CDN播放器-${muted ? "静音" : "取消静音"}`,
        level: "info",
        tag: "cdn_player_mute",
        extra: {
          actioin: muted ? "mute" : "unmute",
          component: "WebRTCPlayer",
          muted: muted,
        },
      });
    } catch (error) {
      console.error("[WebRTCPlayer] 设置静音失败:", error);
    }
  }

  /**
   * 取消静音
   * @returns {Promise}
   */
  async unmute() {
    return new Promise((resolve, reject) => {
      try {
        this.setMuted(false);
        this.isMuted = false;
        this.blockedMutedPlaying = false; // 重置 blocked 静音播放标志

        // 在用户手势上下文中重新调用 play()，确保视频渲染恢复
        if (this.player && typeof this.player.play === "function") {
          const playResult = this.player.play();
          if (playResult && typeof playResult.catch === "function") {
            playResult.catch((err) => {
              console.warn("[WebRTCPlayer] unmute后play被拒绝:", err?.name, err?.message);
            });
          }
        } else if (this.videoElement) {
          this.videoElement.play().catch((err) => {
            console.warn("[WebRTCPlayer] unmute后video.play被拒绝:", err?.name, err?.message);
          });
        }

        // 取消静音后，调用 onPlaying 回调隐藏按钮
        if (this.onPlaying && typeof this.onPlaying === "function") {
          this.onPlaying();
        }

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }
}

export default WebRTCPlayer;
